import { salesBudgetPlanDailyInput, updateSalesBudgetDailyInput } from 'api/salesBudgetYear';
import moment from 'moment';
import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import SalesBudgetMonthDomain, {
  AnalyticalClassificationType,
  BudgetDailyItemData,
  BudgetType,
  BusinessStatus,
  DayType,
  SalesRatioRoundingType,
} from 'domain/master/storeManagement/salesBudgetDailyInput';
import useToastNotification from 'hooks/useToastNotification';
import { SalesBudgetMonthType } from 'components/organismos/master/storeManagement/salesBudgetYear/SalesBudgetDailyInputLayout/type';
import useDebounce from 'hooks/useDebounce';
import {
  calcBreakEventPointCustomer,
  calcBreakEventPointSale,
  calcContributionMargin,
  calcContributionMarginRate,
  calcEmployeeAndPartCostRate,
  calcFLACost,
  calcFLACostRate,
  calcFLAROCost,
  calcFLAROCostRate,
  calcGrossProfit,
  calcLaborRate,
  calcMarginalProfit,
  calcMarginalProfitRate,
  calcNumberOfCustomers,
  calcOperatingIncome,
  calcOperatingIncomeRate,
  calcOtherCostRate,
  calcProfitLossCost,
  calcProfitLossCostRate,
  calcTurnoverRate,
  getMonthlyTotalCostList,
} from 'components/organismos/master/storeManagement/salesBudgetYear/util';

export const useSalesBudgetMonth = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [salesBudget, setSalesBudget] = useState<SalesBudgetMonthType>({
    listOfOperatingIncome: [],
    listOfRateOperatingIncome: [],
    listNumberOfCustomers: [],
    listOfTurnoverRate: [],
    listOfMarginalProfit: [],
    listRateOfMarginalProfit: [],
    listOfRateContributionMargin: [],
    listOfBreakEventPointSale: [],
    listOfBreakEventPointNumber: [],
    listOfLaborShare: [],
    listOfFLACost: [],
    listOfFLACostRate: [],
    listOfProfitLossCost: [],
    listOfProfitLossCostRate: [],
    listOfFLAROCost: [],
    listOfFLAROCostRate: [],
    listOfContributionMargin: [],
    listSumOfOtherTotalCostRate: [],
    listSumOfAdvertisingSaleAndSaleBudgetFree: [],
    listSumOfFoodCostAndDrinkCost: [],
    listSumOfAdvertisingExpenseEachDay: [],
    listSumOfEmployeeAndPartCostRatio: [],
    listSumOfOtherTotalCost: [],
    listSumOfRentEachDay: [],
    listSumOfEmployeeLaborCostAndPartLaborCost: [],
    sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow: 0,
    sumOfTotalEmployeeLaborCostAndPartLaborCost: 0,
    sumOfAdvertisingExpense: 0,
    sumOfLandRentRow: 0,
    sumOfFoodCostAndDrinkCost: 0,
    sumOfNumberOfCustomers: 0,
    sumOfTurnoverRate: 0,
    sumOfMarginalProfit: 0,
    sumOfRateMarginalProfit: 0,
    sumOfContributionMargin: 0,
    sumOfRateContributionMargin: 0,
    sumOfBreakEventPointSale: 0,
    sumOfBreakEventPointNumber: 0,
    sumOfSumOfEmployeeAndPartCostRatio: 0,
    sumOfLaborShare: 0,
    sumOfFLACostRate: 0,
    sumOfProfitLossCost: 0,
    sumOfProfitLossCostRate: 0,
    sumOfFLAROCost: 0,
    sumOfFLACost: 0,
    sumOfOtherTotalCost: 0,
    sumOfOtherTotalCostRate: 0,
    totalOfOperatingIncome: 0,
    rateOfOperationIncome: 0,
  });

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { successNotification, errorNotification } = useToastNotification();

  const FUNCTION_INTERVAL = 500;

  const onSubmit = async (values: SalesBudgetMonthDomain) => {
    setConfirmModalOpen(false);

    if (!confirmModalOpen) {
      setConfirmModalOpen(true);
      setIsLoading(false);
    } else {
      setIsLoading(true);
      const {
        salesPlanDailyDateData,
        monthLtSales,
        monthLtCost,
        monthLtLabor,
        monthLtAdvertising,
        monthLtRent,
        monthLtExpenses,
        monthLtOther,
        monthLtCustomPrice,
      } = values;

      const budgetInputDataList = [
        ...(monthLtSales ?? []),
        ...(monthLtCost ?? []),
        ...(monthLtLabor ?? []),
        ...(monthLtAdvertising ?? []),
        ...(monthLtRent ?? []),
        ...(monthLtExpenses ?? []),
        ...(monthLtOther ?? []),
        ...(monthLtCustomPrice ? [monthLtCustomPrice] : []),
        // NOTE: 新しく分析分類区分を追加した場合はここに対象の配列を追加する
      ]?.map((data) => ({
        analyticalItemCode: data.analyticalItemCode,
        analyticalItemName: data.analyticalItemName,
        analyticalItemId: data.analyticalItemId,
        advertisingMediaMstCode: data.advertisingMediaMstCode,
        analyticalClassificationType: data.analyticalClassificationType,
        budgetType: data.budgetType,
        totalAmount: data.dayTotal,
        dailyAmountList: data.dayAryList,
        dailyDistributionValue: data.dayAryList[getFirstDistributionDateIndex(data.budgetDiscributionType)],
        dailyDistributionRoundingValue: data.dayAryList[getDistributionRoundingDateIndex(data.budgetDiscributionType)],
        dailyDistributionRoundingDate:
          salesPlanDailyDateData[getDistributionRoundingDateIndex(data.budgetDiscributionType)].day,
        expensesClassification: data.expensesClassification,
        sumFlag: data.sumFlag,
      }));

      const budgetSummaryDataList: {
        analyticalClassificationType: AnalyticalClassificationType;
        totalAmount: number;
        dailyAmountList: number[];
      }[] = [
        {
          analyticalClassificationType: AnalyticalClassificationType.S_SALES, // 売上
          totalAmount: salesBudget.sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
          dailyAmountList: salesBudget.listSumOfAdvertisingSaleAndSaleBudgetFree,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.F_FOOD_COST, // 食材費
          totalAmount: salesBudget.sumOfFoodCostAndDrinkCost,
          dailyAmountList: salesBudget.listSumOfFoodCostAndDrinkCost,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.L_LABOR_COST, // 人件費
          totalAmount: salesBudget.sumOfTotalEmployeeLaborCostAndPartLaborCost,
          dailyAmountList: salesBudget.listSumOfEmployeeLaborCostAndPartLaborCost,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.A_ADVERTISING_COST, // 広告費
          totalAmount: salesBudget.sumOfAdvertisingExpense,
          dailyAmountList: salesBudget.listSumOfAdvertisingExpenseEachDay,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.R_RENT, // 家賃
          totalAmount: salesBudget.sumOfLandRentRow,
          dailyAmountList: salesBudget.listSumOfRentEachDay,
        },
        {
          analyticalClassificationType: AnalyticalClassificationType.O_OTHER_EXPENSES, // その他費用
          totalAmount: salesBudget.sumOfOtherTotalCost,
          dailyAmountList: salesBudget.listSumOfOtherTotalCost,
        },
        // NOTE: 新しく分析分類区分を追加した場合はここに対象の配列を追加する
      ];

      const orgCode = sessionStorage.getItem('orgCode_DestinationOrg') || '';
      const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
      const targetMonth = `${sessionStorage.getItem('month_selected')}` || '';
      const updateUser = sessionStorage.getItem('loginUser.staffName') || '';
      const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || '';

      const storeHolidayList = salesPlanDailyDateData
        .filter((data) => data.status === BusinessStatus.Closed)
        .map((data) => data.day);

      updateSalesBudgetDailyInput(companyCode, {
        orgCode,
        targetMonth: moment(targetMonth, 'YYYY/MM/DD').format('YYYY-MM-DD'),
        loginStaffCode,
        updateUser,
        storeHolidayList,
        budgetInputDataList,
        budgetSummaryDataList,
      })
        .then((response) => {
          setConfirmModalOpen(false);
          successNotification('登録しました。');
        })
        .catch((error: any) => {
          if (error.response?.data?.data?.errors) {
            const listErr = error.response.data.data.errors;

            listErr.forEach(
              (e: {
                analyticalClassificationType: AnalyticalClassificationType | 'C';
                analyticalItemIndex: number;
                budgetType: BudgetType;
                dayArrayIndex: number | null;
                message: string;
              }) => {
                let category = '';
                switch (e.analyticalClassificationType) {
                  case AnalyticalClassificationType.S_SALES:
                    category = 'monthLtSales';
                    break;
                  case AnalyticalClassificationType.F_FOOD_COST:
                    category = 'monthLtCost';
                    break;
                  case AnalyticalClassificationType.L_LABOR_COST:
                    category = 'monthLtLabor';
                    break;
                  case AnalyticalClassificationType.A_ADVERTISING_COST:
                    category = 'monthLtAdvertising';
                    break;
                  case AnalyticalClassificationType.R_RENT:
                    category = 'monthLtRent';
                    break;
                  case AnalyticalClassificationType.O_OTHER_EXPENSES:
                    category = 'monthLtExpenses';
                    break;
                  case AnalyticalClassificationType.NT_NON_INCLUDED:
                    category = 'monthLtOther';
                    break;
                  case 'C':
                    category = 'monthLtCustomPrice';
                    break;
                  default:
                    break;
                }

                const subCategory = e.budgetType === BudgetType.DAILY_INPUT ? 'dayAryList' : 'dayTotal';

                formik.setFieldError(
                  `${category}[${e.analyticalItemIndex}].${subCategory}${
                    e.dayArrayIndex !== null ? `.[${e.dayArrayIndex}]` : ''
                  }`,
                  e.message,
                );
              },
            );
          } else {
            errorNotification('サーバー側でエラーが発生しました。');
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const formik = useFormik({
    initialValues: SalesBudgetMonthDomain.generateInitial(),
    onSubmit,
  });

  useEffect(() => {
    const orgCode = sessionStorage.getItem('orgCode_DestinationOrg');
    const targetMonth = sessionStorage.getItem('month_selected');
    const convertTargetMonth = moment(targetMonth?.toString()).format('YYYY/MM/DD');

    salesBudgetPlanDailyInput(orgCode, convertTargetMonth).then((response: any) => {
      formik.setFieldValue('salesPlanDailyDateData', response.salesPlanDailyDateData);
      formik.setFieldValue('monthLtSales', response.monthLtSales);
      formik.setFieldValue('monthLtCost', response.monthLtCost);
      formik.setFieldValue('monthLtLabor', response.monthLtLabor);
      formik.setFieldValue('monthLtAdvertising', response.monthLtAdvertising);
      formik.setFieldValue('monthLtRent', response.monthLtRent);
      formik.setFieldValue('monthLtExpenses', response.monthLtExpenses);
      formik.setFieldValue('monthLtOther', response.monthLtOther);
      formik.setFieldValue('monthLtCustomPrice', response.monthLtCustomPrice);

      setTimeout(() => {
        calcAllValues();
      }, 500);
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
  };

  const calculatorTableDaily = () => {
    const {
      salesPlanDailyDateData,
      monthLtSales,
      monthLtCost,
      monthLtLabor,
      monthLtAdvertising,
      monthLtRent,
      monthLtExpenses,
      // perCustomerPrice,
      monthLtCustomPrice,
    } = formik.values;

    let listCalcAdvertisingSaleAndSaleBudgetFree = Array.from({ length: salesPlanDailyDateData.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (monthLtSales !== null) {
      listCalcAdvertisingSaleAndSaleBudgetFree = getMonthlyTotalCostList(monthLtSales, salesPlanDailyDateData);
    }

    let listSumOfFoodCostAndDrinkCost = Array.from({ length: salesPlanDailyDateData.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (monthLtCost !== null) {
      listSumOfFoodCostAndDrinkCost = getMonthlyTotalCostList(monthLtCost, salesPlanDailyDateData);
    }

    let listSumOfEmployeeLaborCostAndPartLaborCost = Array.from({ length: salesPlanDailyDateData.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (monthLtLabor !== null) {
      listSumOfEmployeeLaborCostAndPartLaborCost = getMonthlyTotalCostList(monthLtLabor, salesPlanDailyDateData);
    }

    let listSumOfAdvertisingExpenseEachDay = Array.from({ length: salesPlanDailyDateData.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (monthLtAdvertising !== null) {
      listSumOfAdvertisingExpenseEachDay = getMonthlyTotalCostList(monthLtAdvertising, salesPlanDailyDateData);
    }

    let listSumOfRentEachDay = Array.from({ length: salesPlanDailyDateData.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (monthLtRent !== null) {
      listSumOfRentEachDay = getMonthlyTotalCostList(monthLtRent, salesPlanDailyDateData);
    }

    let listSumOfOtherTotalCost = Array.from({ length: salesPlanDailyDateData.length }, () => ({
      totalCost: 0,
      totalVariableCost: 0,
      totalFixedCost: 0,
    }));

    if (monthLtExpenses !== null) {
      listSumOfOtherTotalCost = getMonthlyTotalCostList(monthLtExpenses, salesPlanDailyDateData);
    }

    const listSumOfFixedCost: number[] = Array.from({ length: salesPlanDailyDateData.length }, () => 0);

    const listSumOfVariableCost: number[] = Array.from({ length: salesPlanDailyDateData.length }, () => 0);

    // 固定費と変動費の合計を計算
    salesPlanDailyDateData.forEach((_, i) => {
      listSumOfFixedCost[i] =
        listSumOfEmployeeLaborCostAndPartLaborCost[i].totalFixedCost +
        listSumOfFoodCostAndDrinkCost[i].totalFixedCost +
        listSumOfAdvertisingExpenseEachDay[i].totalFixedCost +
        listSumOfRentEachDay[i].totalFixedCost +
        listSumOfOtherTotalCost[i].totalFixedCost;

      listSumOfVariableCost[i] =
        listSumOfEmployeeLaborCostAndPartLaborCost[i].totalVariableCost +
        listSumOfFoodCostAndDrinkCost[i].totalVariableCost +
        listSumOfAdvertisingExpenseEachDay[i].totalVariableCost +
        listSumOfRentEachDay[i].totalVariableCost +
        listSumOfOtherTotalCost[i].totalVariableCost;
    });

    // 初期化
    const listSumOfOtherTotalCostRate = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listNumberOfCustomers = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listGrossProfit = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfTurnoverRate = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfMarginalProfit = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listRateOfMarginalProfit = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfContributionMargin = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfRateContributionMargin = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfBreakEventPointSale = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfBreakEventPointNumber = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listSumOfEmployeeAndPartCostRatio = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfLaborShare = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfFLACost = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfFLACostRate = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfProfitLossCost = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfProfitLossCostRate = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfFLAROCost = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfFLAROCostRate = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfOperatingIncome = Array.from({ length: salesPlanDailyDateData.length }, () => 0);
    const listOfRateOperatingIncome = Array.from({ length: salesPlanDailyDateData.length }, () => 0);

    let numberOfSeat = 0;

    salesPlanDailyDateData.forEach((data, i) => {
      if (numberOfSeat === 0 && data.numberOfSeat > 0) {
        numberOfSeat = data.numberOfSeat;
      }

      // その他経費率
      listSumOfOtherTotalCostRate[i] = calcOtherCostRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listSumOfOtherTotalCost[i].totalCost,
      );

      // 客数
      listNumberOfCustomers[i] = calcNumberOfCustomers(
        monthLtCustomPrice?.dayAryList[i] ?? 0,
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
      );

      // 粗利益
      listGrossProfit[i] = calcGrossProfit(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listSumOfFoodCostAndDrinkCost[i].totalCost,
      );

      // 回転率
      listOfTurnoverRate[i] = calcTurnoverRate(numberOfSeat, listNumberOfCustomers[i]) * 100;

      // 限界利益額
      listOfMarginalProfit[i] = calcMarginalProfit(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listSumOfVariableCost[i],
      );

      // 限界利益率
      listRateOfMarginalProfit[i] = calcMarginalProfitRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfMarginalProfit[i],
      );

      // 貢献利益額
      listOfContributionMargin[i] = calcContributionMargin(listOfMarginalProfit[i], listSumOfFixedCost[i]);

      // 貢献利益率
      listOfRateContributionMargin[i] = calcContributionMarginRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfContributionMargin[i],
      );

      // 損益分岐点 売上
      listOfBreakEventPointSale[i] = calcBreakEventPointSale(listRateOfMarginalProfit[i], listSumOfFixedCost[i]);

      // 損益分岐点 客数
      listOfBreakEventPointNumber[i] = calcBreakEventPointCustomer(
        monthLtCustomPrice?.dayAryList[i] ?? 0,
        listOfBreakEventPointSale[i],
      );

      // 人件費率 合計
      listSumOfEmployeeAndPartCostRatio[i] = calcEmployeeAndPartCostRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listSumOfEmployeeLaborCostAndPartLaborCost[i].totalCost,
      );

      // 人件費率
      listOfLaborShare[i] = calcLaborRate(listGrossProfit[i], listSumOfEmployeeLaborCostAndPartLaborCost[i].totalCost);

      // FLA合計
      listOfFLACost[i] = calcFLACost(
        listSumOfFoodCostAndDrinkCost[i].totalCost,
        listSumOfEmployeeLaborCostAndPartLaborCost[i].totalCost,
        listSumOfAdvertisingExpenseEachDay[i].totalCost,
      );

      // FLA率
      listOfFLACostRate[i] = calcFLACostRate(listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost, listOfFLACost[i]);

      // 店舗損益額
      listOfProfitLossCost[i] = calcProfitLossCost(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfFLACost[i],
      );

      // 店舗損益率
      listOfProfitLossCostRate[i] = calcProfitLossCostRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfProfitLossCost[i],
      );

      // FLARO合計
      listOfFLAROCost[i] = calcFLAROCost(
        listOfFLACost[i],
        listSumOfRentEachDay[i].totalCost,
        listSumOfOtherTotalCost[i].totalCost,
      );

      // FLARO率
      listOfFLAROCostRate[i] = calcFLAROCostRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfFLAROCost[i],
      );

      // 営業利益
      listOfOperatingIncome[i] = calcOperatingIncome(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfFLAROCost[i],
      );

      // 営業利益率
      listOfRateOperatingIncome[i] = calcOperatingIncomeRate(
        listCalcAdvertisingSaleAndSaleBudgetFree[i].totalCost,
        listOfOperatingIncome[i],
      );
    });

    // 合計値を計算
    const sumOfContributionMargin = listOfContributionMargin.reduce((acc, val) => acc + val, 0);
    const sumOfBreakEventPointSale = listOfBreakEventPointSale.reduce((acc, val) => acc + val, 0);
    const sumOfBreakEventPointNumber = listOfBreakEventPointNumber.reduce((acc, val) => acc + val, 0);
    const sumOfFLAROCost = listOfFLAROCost.reduce((acc, val) => acc + val, 0);
    const sumOfFLACost = listOfFLACost.reduce((acc, val) => acc + val, 0);
    const sumOfProfitLossCost = listOfProfitLossCost.reduce((acc, val) => acc + val, 0);
    const sumOfOtherTotalCost = listSumOfOtherTotalCost.reduce((acc, val) => acc + val.totalCost, 0);
    const sumOfFoodCostAndDrinkCost = listSumOfFoodCostAndDrinkCost.reduce((acc, val) => acc + val.totalCost, 0);
    const sumOfLandRentRow = listSumOfRentEachDay.reduce((acc, val) => acc + val.totalCost, 0);
    const sumOfAdvertisingExpense = listSumOfAdvertisingExpenseEachDay.reduce((acc, val) => acc + val.totalCost, 0);
    const sumOfTotalEmployeeLaborCostAndPartLaborCost = listSumOfEmployeeLaborCostAndPartLaborCost.reduce(
      (acc, val) => acc + val.totalCost,
      0,
    );
    const sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow = listCalcAdvertisingSaleAndSaleBudgetFree.reduce(
      (acc, val) => acc + val.totalCost,
      0,
    );
    const sumOfNumberOfCustomers = listNumberOfCustomers.reduce((acc, val) => acc + val, 0);
    const sumOfMarginalProfit = listOfMarginalProfit.reduce((acc, val) => acc + val, 0);
    const sumOfGrossProfit = listGrossProfit.reduce((acc, val) => acc + val, 0);

    const sumOfRateMarginalProfit = calcMarginalProfitRate(
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfMarginalProfit,
    );
    const sumOfTurnoverRate = numberOfSeat ? sumOfNumberOfCustomers / numberOfSeat : 0;
    const sumOfOtherTotalCostRate = calcOtherCostRate(
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfOtherTotalCost,
    );
    const sumOfFLACostRate = calcFLACostRate(sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow, sumOfFLACost);
    const sumOfProfitLossCostRate = sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
      ? sumOfProfitLossCost / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
      : 0;
    const sumOfRateContributionMargin = calcContributionMarginRate(
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfContributionMargin,
    );
    const sumOfSumOfEmployeeAndPartCostRatio = calcEmployeeAndPartCostRate(
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfTotalEmployeeLaborCostAndPartLaborCost,
    );
    const sumOfLaborShare = calcLaborRate(sumOfGrossProfit, sumOfTotalEmployeeLaborCostAndPartLaborCost);

    const totalOfOperatingIncome = sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow - sumOfFLAROCost;
    const rateOfOperationIncome = sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
      ? totalOfOperatingIncome / sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow
      : 0;

    setSalesBudget({
      listOfOperatingIncome,
      listOfRateOperatingIncome,
      listNumberOfCustomers,
      listOfTurnoverRate,
      listOfMarginalProfit,
      listRateOfMarginalProfit,
      listOfContributionMargin,
      listOfRateContributionMargin,
      listOfBreakEventPointSale,
      listOfBreakEventPointNumber,
      listOfLaborShare,
      listOfFLACost,
      listOfFLACostRate,
      listOfProfitLossCost,
      listOfProfitLossCostRate,
      listOfFLAROCost,
      listOfFLAROCostRate,
      listSumOfAdvertisingSaleAndSaleBudgetFree: listCalcAdvertisingSaleAndSaleBudgetFree.map((item) => item.totalCost),
      listSumOfOtherTotalCost: listSumOfOtherTotalCost.map((item) => item.totalCost),
      listSumOfEmployeeLaborCostAndPartLaborCost: listSumOfEmployeeLaborCostAndPartLaborCost.map(
        (item) => item.totalCost,
      ),
      listSumOfFoodCostAndDrinkCost: listSumOfFoodCostAndDrinkCost.map((item) => item.totalCost),
      listSumOfAdvertisingExpenseEachDay: listSumOfAdvertisingExpenseEachDay.map((item) => item.totalCost),
      listSumOfRentEachDay: listSumOfRentEachDay.map((item) => item.totalCost),
      listSumOfOtherTotalCostRate,
      listSumOfEmployeeAndPartCostRatio,
      sumOfAdvertisingSaleAndSumOfSaleBudgetFreeRow,
      sumOfTotalEmployeeLaborCostAndPartLaborCost,
      sumOfAdvertisingExpense,
      sumOfLandRentRow,
      sumOfFoodCostAndDrinkCost,
      sumOfNumberOfCustomers,
      sumOfTurnoverRate,
      sumOfMarginalProfit,
      sumOfRateMarginalProfit,
      sumOfContributionMargin,
      sumOfRateContributionMargin,
      sumOfBreakEventPointSale,
      sumOfBreakEventPointNumber,
      sumOfSumOfEmployeeAndPartCostRatio,
      sumOfLaborShare,
      sumOfFLACostRate,
      sumOfProfitLossCost,
      sumOfProfitLossCostRate,
      sumOfFLAROCost,
      sumOfFLACost,
      sumOfOtherTotalCost,
      sumOfOtherTotalCostRate,
      totalOfOperatingIncome,
      rateOfOperationIncome,
    });
  };

  // 日次按分方法によって、対象日数を計算する
  const getDistributionDateCount = (budgetDistributionType: DayType) => {
    const { salesPlanDailyDateData } = formik.values;

    // 歴日の場合はそのままカレンダー日数を返す
    if (budgetDistributionType === DayType.CALENDAR_DAY) {
      return salesPlanDailyDateData.length;
    }

    // 営業日数を計算し、返す
    return formik.values.salesPlanDailyDateData.reduce((count, data) => (data.status !== BusinessStatus.Closed ? count + 1 : count), 0);
  };

  // 日次按分方法によって、按分を実施する日付を算出する
  const getDistributionRoundingDateIndex = (budgetDistributionType: DayType) => {
    const { salesPlanDailyDateData } = formik.values;

    // 歴日の場合はそのままカレンダー日数を返す
    if (budgetDistributionType === DayType.CALENDAR_DAY) {
      return salesPlanDailyDateData.length - 1;
    }

    // 営業日数の場合は、最終営業日のインデックスを返す
    const reverseArray = [...formik.values.salesPlanDailyDateData].reverse();
    return salesPlanDailyDateData.length - 1 - reverseArray.findIndex((data) => data.status !== BusinessStatus.Closed);
  };

  // 日次按分方法によって、最初の営業日のインデックスを算出する
  const getFirstDistributionDateIndex = (budgetDistributionType: DayType) => {
    const { salesPlanDailyDateData } = formik.values;

    // 歴日の場合は最初の日（インデックス0）を返す
    if (budgetDistributionType === DayType.CALENDAR_DAY) {
      return 0;
    }

    // 営業日数の場合は、最初の営業日のインデックスを返す
    return salesPlanDailyDateData.findIndex((data) => data.status !== BusinessStatus.Closed);
  };

  // 端数処理
  function roundNumber(value: number, roundingType: SalesRatioRoundingType) {
    switch (roundingType) {
      case 0: // 切り捨て
        return Math.floor(value);
      case 1: // 切り上げ
        return Math.ceil(value);
      case 2: // 四捨五入
        return Math.round(value);
      default:
        throw new Error('Invalid rounding method');
    }
  }

  const handleChangeSelect = (changeSelectIndex: number) => {
    const {
      salesPlanDailyDateData,
      monthLtSales,
      monthLtCost,
      monthLtLabor,
      monthLtAdvertising,
      monthLtRent,
      monthLtExpenses,
      monthLtOther,
      monthLtCustomPrice,
    } = formik.values;

    // すべての入力項目を再計算

    monthLtSales?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.DAILY_INPUT:
          if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
            monthLtSales![index].dayAryList[changeSelectIndex] = 0;
          }
          break;
      }
    });

    formik.setFieldValue('monthLtSales', monthLtSales);

    monthLtCost?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.DAILY_INPUT:
          if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
            monthLtCost![index].dayAryList[changeSelectIndex] = 0;
          }
          break;
      }
    });
    formik.setFieldValue('monthLtCost', monthLtCost);

    monthLtLabor?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.DAILY_INPUT:
          if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
            monthLtLabor![index].dayAryList[changeSelectIndex] = 0;
          }
          break;
      }
    });
    formik.setFieldValue('monthLtLabor', monthLtLabor);

    monthLtAdvertising?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.DAILY_INPUT:
          if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
            monthLtAdvertising![index].dayAryList[changeSelectIndex] = 0;
          }
          break;
      }
    });
    formik.setFieldValue('monthLtAdvertising', monthLtAdvertising);

    monthLtRent?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.DAILY_INPUT:
          if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
            monthLtRent![index].dayAryList[changeSelectIndex] = 0;
          }
          break;
      }
    });
    formik.setFieldValue('monthLtRent', monthLtRent);

    monthLtExpenses?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.DAILY_INPUT:
          if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
            monthLtExpenses![index].dayAryList[changeSelectIndex] = 0;
          }
          break;
      }
    });
    formik.setFieldValue('monthLtExpenses', monthLtExpenses);

    monthLtOther?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.DAILY_INPUT:
          if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
            monthLtOther![index].dayAryList[changeSelectIndex] = 0;
          }
          break;
      }
    });
    formik.setFieldValue('monthLtOther', monthLtOther);

    monthLtOther?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.DAILY_INPUT:
          if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
            monthLtOther![index].dayAryList[changeSelectIndex] = 0;
          }
          break;
      }
    });
    formik.setFieldValue('monthLtOther', monthLtOther);

    // if (monthLtCustomPrice?.budgetType === BudgetType.DAILY_INPUT) {
    if (monthLtCustomPrice) {
      if (salesPlanDailyDateData[changeSelectIndex].status === BusinessStatus.Closed) {
        monthLtCustomPrice.dayAryList[changeSelectIndex] = 0;
      }
    }
    // }

    setTimeout(() => {
      calcAllValues();
    }, 500);
  };

  const calcDayValues = (data: BudgetDailyItemData[] | null, targetIndex: number) => {
    const { salesPlanDailyDateData } = formik.values;

    const result =
      data?.map((item, i) => {
        if (i !== targetIndex) return item;

        const distributionRoundingDateIndex = getDistributionRoundingDateIndex(item.budgetDiscributionType);
        const dayValue = roundNumber(
          item.dayTotal / getDistributionDateCount(item.budgetDiscributionType),
          item.salesRatioRounding,
        );

        const calcValues = item.dayAryList.map((_, j) => {
          if (
            salesPlanDailyDateData[j].status === BusinessStatus.Closed &&
            item.budgetDiscributionType === DayType.BUSINESS_DAY
          ) {
            return 0;
          }

          return dayValue;
        });

        // 端数を計上
        calcValues[distributionRoundingDateIndex] += item.dayTotal - calcValues.reduce((acc, val) => acc + val, 0);

        return { ...item, dayAryList: calcValues };
      }) ?? [];

    return result;
  };

  const calcMonthlyValue = (data: BudgetDailyItemData[] | null, targetIndex: number) => {
    const result =
      data?.map((item, index) => {
        if (index !== targetIndex) return item;

        return { ...item, dayTotal: item.dayAryList.reduce((acc, val) => acc + val, 0) };
      }) ?? [];

    return result;
  };

  const calcDaySaleRatioValues = (data: BudgetDailyItemData[] | null, listSumOfItem: number[], targetIndex: number) => {
    const result =
      data?.map((item, i) => {
        if (i !== targetIndex) return item;

        const calcValues = item.dayAryList.map((_, j) => {
          const dayValue = listSumOfItem[j];

          const value = dayValue ? dayValue * item.budgetSalesRatio : 0;

          return roundNumber(value, item.salesRatioRounding);
        });

        const dayTotal = calcValues.reduce((acc, val) => acc + val, 0);

        return { ...item, dayTotal, dayAryList: calcValues };
      }) ?? [];

    return result;
  };

  const calcAllValues = () => {
    const {
      monthLtSales,
      monthLtCost,
      monthLtLabor,
      monthLtAdvertising,
      monthLtRent,
      monthLtExpenses,
      monthLtOther,
      monthLtCustomPrice,
    } = formik.values;

    // すべての入力項目を再計算

    let editMonthLtSales = monthLtSales;

    monthLtSales?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.MONTHLY_INPUT:
          editMonthLtSales = calcDayValues(editMonthLtSales, index);
          break;
        case BudgetType.DAILY_INPUT:
          editMonthLtSales = calcMonthlyValue(editMonthLtSales, index);
          break;
        case BudgetType.YEARLY_INPUT:
          editMonthLtSales = calcYearlyToMonthlyDistribution(editMonthLtSales);
          break;
      }
    });

    formik.setFieldValue('monthLtSales', editMonthLtSales);

    let editMonthLtCost = monthLtCost;

    monthLtCost?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.MONTHLY_INPUT:
          editMonthLtCost = calcDayValues(editMonthLtCost, index);
          break;
        case BudgetType.DAILY_INPUT:
          editMonthLtCost = calcMonthlyValue(editMonthLtCost, index);
          break;
        case BudgetType.YEARLY_INPUT:
          editMonthLtCost = calcYearlyToMonthlyDistribution(editMonthLtCost);
          break;
      }
    });
    formik.setFieldValue('monthLtCost', editMonthLtCost);

    let editMonthLtLabor = monthLtLabor;

    monthLtLabor?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.MONTHLY_INPUT:
          editMonthLtLabor = calcDayValues(editMonthLtLabor, index);
          break;
        case BudgetType.DAILY_INPUT:
          editMonthLtLabor = calcMonthlyValue(editMonthLtLabor, index);
          break;
        case BudgetType.YEARLY_INPUT:
          editMonthLtLabor = calcYearlyToMonthlyDistribution(editMonthLtLabor);
          break;
      }
    });
    formik.setFieldValue('monthLtLabor', editMonthLtLabor);

    let editMonthLtAdvertising = monthLtAdvertising;

    monthLtAdvertising?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.MONTHLY_INPUT:
          editMonthLtAdvertising = calcDayValues(editMonthLtAdvertising, index);
          break;
        case BudgetType.DAILY_INPUT:
          editMonthLtAdvertising = calcMonthlyValue(editMonthLtAdvertising, index);
          break;
        case BudgetType.YEARLY_INPUT:
          editMonthLtAdvertising = calcYearlyToMonthlyDistribution(editMonthLtAdvertising);
          break;
      }
    });
    formik.setFieldValue('monthLtAdvertising', editMonthLtAdvertising);

    let editMonthLtRent = monthLtRent;

    monthLtRent?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.MONTHLY_INPUT:
          editMonthLtRent = calcDayValues(editMonthLtRent, index);
          break;
        case BudgetType.DAILY_INPUT:
          editMonthLtRent = calcMonthlyValue(editMonthLtRent, index);
          break;
        case BudgetType.YEARLY_INPUT:
          editMonthLtRent = calcYearlyToMonthlyDistribution(editMonthLtRent);
          break;
      }
    });

    formik.setFieldValue('monthLtRent', editMonthLtRent);

    let editMonthLtExpenses = monthLtExpenses;

    monthLtExpenses?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.MONTHLY_INPUT:
          editMonthLtExpenses = calcDayValues(editMonthLtExpenses, index);
          break;
        case BudgetType.DAILY_INPUT:
          editMonthLtExpenses = calcMonthlyValue(editMonthLtExpenses, index);
          break;
        case BudgetType.YEARLY_INPUT:
          editMonthLtExpenses = calcYearlyToMonthlyDistribution(editMonthLtExpenses);
          break;
      }
    });
    formik.setFieldValue('monthLtExpenses', editMonthLtExpenses);

    let editMonthLtOther = monthLtOther;

    monthLtOther?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.MONTHLY_INPUT:
          editMonthLtOther = calcDayValues(editMonthLtOther, index);
          break;
        case BudgetType.DAILY_INPUT:
          editMonthLtOther = calcMonthlyValue(editMonthLtOther, index);
          break;
        case BudgetType.YEARLY_INPUT:
          editMonthLtOther = calcYearlyToMonthlyDistribution(editMonthLtOther);
          break;
      }
    });
    formik.setFieldValue('monthLtOther', editMonthLtOther);

    // if (monthLtCustomPrice?.budgetType === BudgetType.MONTHLY_INPUT) {
    //   const dayAryList =
    //     monthLtCustomPrice.dayAryList.map((_, i) => {
    //       if (
    //         salesPlanDailyDateData[i].status === BusinessStatus.Closed &&
    //         monthLtCustomPrice.budgetDiscributionType === DayType.BUSINESS_DAY
    //       ) {
    //         return 0;
    //       }

    //       return monthLtCustomPrice.dayTotal;
    //     }) ?? [];

    //   formik.setFieldValue('monthLtCustomPrice', { ...monthLtCustomPrice, dayAryList });
    // } else if (monthLtCustomPrice?.budgetType === BudgetType.DAILY_INPUT) {
    const { salesPlanDailyDateData } = formik.values;
    const dataLength =
      monthLtCustomPrice?.dayAryList?.filter((item, i) => salesPlanDailyDateData[i].status !== BusinessStatus.Closed && item > 0).length ??
      0;
    const totalCustomPrice =
      monthLtCustomPrice?.dayAryList
        ?.filter((_, i) => salesPlanDailyDateData[i].status !== BusinessStatus.Closed)
        .reduce((acc, cur) => acc + cur, 0) ?? 0;

    const dayTotal = Math.floor(dataLength > 0 ? totalCustomPrice / dataLength : 0);

    formik.setFieldValue('monthLtCustomPrice', { ...monthLtCustomPrice, dayTotal });
    // }

    // 売上自動計算（売上比率）項目の再計算
    // 店休ステータス変更などで売上が変わった後、売上自動計算項目も最新の売上合計に基づいて再計算する
    const newSalesTotals = editMonthLtSales
      ? getMonthlyTotalCostList(editMonthLtSales, salesPlanDailyDateData).map((x) => x.totalCost)
      : Array.from({ length: salesPlanDailyDateData.length }, () => 0);

    monthLtSales?.forEach((item, index) => {
      if (item.budgetType === BudgetType.REVENUE_RATIO && item.sumFlag !== 0) {
        editMonthLtSales = calcDaySaleRatioValues(editMonthLtSales, newSalesTotals, index);
      }
    });
    formik.setFieldValue('monthLtSales', editMonthLtSales);

    monthLtCost?.forEach((item, index) => {
      if (item.budgetType === BudgetType.REVENUE_RATIO && item.sumFlag !== 0) {
        editMonthLtCost = calcDaySaleRatioValues(editMonthLtCost, newSalesTotals, index);
      }
    });
    formik.setFieldValue('monthLtCost', editMonthLtCost);

    monthLtLabor?.forEach((item, index) => {
      if (item.budgetType === BudgetType.REVENUE_RATIO && item.sumFlag !== 0) {
        editMonthLtLabor = calcDaySaleRatioValues(editMonthLtLabor, newSalesTotals, index);
      }
    });
    formik.setFieldValue('monthLtLabor', editMonthLtLabor);

    monthLtAdvertising?.forEach((item, index) => {
      if (item.budgetType === BudgetType.REVENUE_RATIO && item.sumFlag !== 0) {
        editMonthLtAdvertising = calcDaySaleRatioValues(editMonthLtAdvertising, newSalesTotals, index);
      }
    });
    formik.setFieldValue('monthLtAdvertising', editMonthLtAdvertising);

    monthLtRent?.forEach((item, index) => {
      if (item.budgetType === BudgetType.REVENUE_RATIO && item.sumFlag !== 0) {
        editMonthLtRent = calcDaySaleRatioValues(editMonthLtRent, newSalesTotals, index);
      }
    });
    formik.setFieldValue('monthLtRent', editMonthLtRent);

    monthLtExpenses?.forEach((item, index) => {
      if (item.budgetType === BudgetType.REVENUE_RATIO && item.sumFlag !== 0) {
        editMonthLtExpenses = calcDaySaleRatioValues(editMonthLtExpenses, newSalesTotals, index);
      }
    });
    formik.setFieldValue('monthLtExpenses', editMonthLtExpenses);

    monthLtOther?.forEach((item, index) => {
      if (item.budgetType === BudgetType.REVENUE_RATIO && item.sumFlag !== 0) {
        editMonthLtOther = calcDaySaleRatioValues(editMonthLtOther, newSalesTotals, index);
      }
    });
    formik.setFieldValue('monthLtOther', editMonthLtOther);

    if (monthLtCustomPrice?.budgetType === BudgetType.REVENUE_RATIO) {
      calcLtCustomPriceDailySaleRatioValues();
    }

    calculatorTableDaily();
  };

  const calcSalesRatioValues = (listSumOfAdvertisingSaleAndSaleBudgetFree: number[]) => {
    const {
      monthLtSales,
      monthLtCost,
      monthLtLabor,
      monthLtAdvertising,
      monthLtRent,
      monthLtExpenses,
      monthLtOther,
      monthLtCustomPrice,
    } = formik.values;

    // すべての入力項目を再計算
    let editMonthLtSales = monthLtSales;

    monthLtSales?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.REVENUE_RATIO:
          // sumFlag=0の場合は売上比率計算をスキップ
          if (item.sumFlag !== 0) {
            editMonthLtSales = calcDaySaleRatioValues(
              editMonthLtSales,
              listSumOfAdvertisingSaleAndSaleBudgetFree,
              index,
            );
          }
          break;
      }
    });

    formik.setFieldValue('monthLtSales', editMonthLtSales);

    let editMonthLtCost = monthLtCost;

    monthLtCost?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.REVENUE_RATIO:
          // sumFlag=0の場合は売上比率計算をスキップ
          if (item.sumFlag !== 0) {
            editMonthLtCost = calcDaySaleRatioValues(editMonthLtCost, listSumOfAdvertisingSaleAndSaleBudgetFree, index);
          }
          break;
      }
    });
    formik.setFieldValue('monthLtCost', editMonthLtCost);

    let editMonthLtLabor = monthLtLabor;

    monthLtLabor?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.REVENUE_RATIO:
          // sumFlag=0の場合は売上比率計算をスキップ
          if (item.sumFlag !== 0) {
            editMonthLtLabor = calcDaySaleRatioValues(
              editMonthLtLabor,
              listSumOfAdvertisingSaleAndSaleBudgetFree,
              index,
            );
          }
          break;
      }
    });
    formik.setFieldValue('monthLtLabor', editMonthLtLabor);

    let editMonthLtAdvertising = monthLtAdvertising;

    monthLtAdvertising?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.REVENUE_RATIO:
          // sumFlag=0の場合は売上比率計算をスキップ
          if (item.sumFlag !== 0) {
            editMonthLtAdvertising = calcDaySaleRatioValues(
              editMonthLtAdvertising,
              listSumOfAdvertisingSaleAndSaleBudgetFree,
              index,
            );
          }
          break;
      }
    });
    formik.setFieldValue('monthLtAdvertising', editMonthLtAdvertising);

    let editMonthLtRent = monthLtRent;

    monthLtRent?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.REVENUE_RATIO:
          // sumFlag=0の場合は売上比率計算をスキップ
          if (item.sumFlag !== 0) {
            editMonthLtRent = calcDaySaleRatioValues(editMonthLtRent, listSumOfAdvertisingSaleAndSaleBudgetFree, index);
          }
          break;
      }
    });

    formik.setFieldValue('monthLtRent', editMonthLtRent);

    let editMonthLtExpenses = monthLtExpenses;

    monthLtExpenses?.forEach((item, index) => {
      switch (item.budgetType) {
        case BudgetType.REVENUE_RATIO:
          // sumFlag=0の場合は売上比率計算をスキップ
          if (item.sumFlag !== 0) {
            editMonthLtExpenses = calcDaySaleRatioValues(
              editMonthLtExpenses,
              listSumOfAdvertisingSaleAndSaleBudgetFree,
              index,
            );
          }
          break;
      }
    });
    formik.setFieldValue('monthLtExpenses', editMonthLtExpenses);

    let editMonthLtOther = monthLtOther;

    monthLtOther?.forEach((item, index) => {
      if (item.sumFlag !== 0) {
        switch (item.budgetType) {
          case BudgetType.REVENUE_RATIO:
            editMonthLtOther = calcDaySaleRatioValues(
              editMonthLtOther,
              listSumOfAdvertisingSaleAndSaleBudgetFree,
              index,
            );
            break;
        }
      }
    });
    formik.setFieldValue('monthLtOther', editMonthLtOther);

    switch (monthLtCustomPrice?.budgetType) {
      case BudgetType.REVENUE_RATIO:
        calcLtCustomPriceDailySaleRatioValues();
        break;
    }

    setTimeout(() => {
      calculatorTableDaily();
    }, 500);
  };

  // 売上高
  const calcLtSalesDailyValues = useDebounce((targetIndex: number) => {
    const { monthLtSales } = formik.values;

    const result = calcDayValues(monthLtSales, targetIndex);
    formik.setFieldValue('monthLtSales', result);

    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // F(食材費)
  const calcLtCostDailyValues = useDebounce((targetIndex: number) => {
    const { monthLtCost } = formik.values;

    const result = calcDayValues(monthLtCost, targetIndex);
    formik.setFieldValue('monthLtCost', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // L(人件費)
  const calcLtLaborDailyValues = useDebounce((targetIndex: number) => {
    const { monthLtLabor } = formik.values;

    const result = calcDayValues(monthLtLabor, targetIndex);
    formik.setFieldValue('monthLtLabor', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // A(広告費)
  const calcLtAdvertisingDailyValues = useDebounce((targetIndex: number) => {
    const { monthLtAdvertising } = formik.values;

    const result = calcDayValues(monthLtAdvertising, targetIndex);
    formik.setFieldValue('monthLtAdvertising', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // R(家賃)
  const calcLtRentDailyValues = useDebounce((targetIndex: number) => {
    const { monthLtRent } = formik.values;

    const result = calcDayValues(monthLtRent, targetIndex);

    formik.setFieldValue('monthLtRent', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // O(その他経費)
  const calcLtExpensesDailyValues = useDebounce((targetIndex: number, isCalcTable: boolean = true) => {
    const { monthLtExpenses } = formik.values;

    const result = calcDayValues(monthLtExpenses, targetIndex);
    formik.setFieldValue('monthLtExpenses', result);
    if (isCalcTable) {
      calculatorTableDaily();
    }
  }, FUNCTION_INTERVAL);

  // NT(集計外項目)
  const calcLtOtherDailyValues = useDebounce((targetIndex: number) => {
    const { monthLtOther } = formik.values;

    const result = calcDayValues(monthLtOther, targetIndex);
    formik.setFieldValue('monthLtOther', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // 客単価
  const calcLtCustomPriceDailyValues = useDebounce(() => {
    const { monthLtCustomPrice, salesPlanDailyDateData } = formik.values;

    if (monthLtCustomPrice) {
      const dayAryList =
        monthLtCustomPrice.dayAryList.map((_, i) => {
          if (
            salesPlanDailyDateData[i].status === BusinessStatus.Closed &&
            monthLtCustomPrice.budgetDiscributionType === DayType.BUSINESS_DAY
          ) {
            return 0;
          }

          return monthLtCustomPrice.dayTotal;
        }) ?? [];

      formik.setFieldValue('monthLtCustomPrice', { ...monthLtCustomPrice, dayAryList });
      calculatorTableDaily();
    }
  }, FUNCTION_INTERVAL);

  // 年次データを月次データに按分する関数
  const calcYearlyToMonthlyDistribution = (monthlyData: BudgetDailyItemData[] | null) => {
    if (!monthlyData) return monthlyData;

    return monthlyData.map((monthItem) => {
      // 予算区分が年次入力の場合のみ按分処理を実行
      if (monthItem.budgetType !== BudgetType.YEARLY_INPUT) {
        return monthItem;
      }

      // dayTotalの値を日次データに按分する
      const distributionDateCount = getDistributionDateCount(monthItem.budgetDiscributionType);
      const distributionRoundingDateIndex = getDistributionRoundingDateIndex(monthItem.budgetDiscributionType);

      // 月次の合計値を日数で按分
      const dailyValue = roundNumber(monthItem.dayTotal / distributionDateCount, monthItem.salesRatioRounding);

      const calcValues = monthItem.dayAryList.map((_, j) => {
        const { salesPlanDailyDateData } = formik.values;
        if (
          salesPlanDailyDateData[j].status === BusinessStatus.Closed &&
          monthItem.budgetDiscributionType === DayType.BUSINESS_DAY
        ) {
          return 0;
        }
        return dailyValue;
      });

      // 営業日のみの合計を計算（定休日は除外）
      const businessDayTotal = calcValues.reduce((acc, val) => acc + val, 0);

      // 端数を計上（営業日按分の場合のみ営業日分の端数を調整）
      const roundingAmount = monthItem.dayTotal - businessDayTotal;
      calcValues[distributionRoundingDateIndex] += roundingAmount;

      return {
        ...monthItem,
        dayAryList: calcValues,
      };
    });
  };

  // 売上高
  const calcLtSalesDailySaleRatioValues = useDebounce(
    (listSumOfAdvertisingSaleAndSaleBudgetFree: number[], targetIndex: number) => {
      const { monthLtSales } = formik.values;

      const result = calcDaySaleRatioValues(monthLtSales, listSumOfAdvertisingSaleAndSaleBudgetFree, targetIndex);
      formik.setFieldValue('monthLtSales', result);
    },
    FUNCTION_INTERVAL,
  );

  // F(食材費)
  const calcLtCostDailySaleRatioValues = useDebounce(
    (listSumOfAdvertisingSaleAndSaleBudgetFree: number[], targetIndex: number) => {
      const { monthLtCost } = formik.values;

      const result = calcDaySaleRatioValues(monthLtCost, listSumOfAdvertisingSaleAndSaleBudgetFree, targetIndex);
      formik.setFieldValue('monthLtCost', result);
    },
    FUNCTION_INTERVAL,
  );

  // L(人件費)
  const calcLtLaborDailySaleRatioValues = useDebounce(
    (listSumOfAdvertisingSaleAndSaleBudgetFree: number[], targetIndex: number) => {
      const { monthLtLabor } = formik.values;

      const result = calcDaySaleRatioValues(monthLtLabor, listSumOfAdvertisingSaleAndSaleBudgetFree, targetIndex);
      formik.setFieldValue('monthLtLabor', result);
    },
    FUNCTION_INTERVAL,
  );

  // A(広告費)
  const calcLtAdvertisingDailySaleRatioValues = useDebounce(
    (listSumOfAdvertisingSaleAndSaleBudgetFree: number[], targetIndex: number) => {
      const { monthLtAdvertising } = formik.values;

      const result = calcDaySaleRatioValues(monthLtAdvertising, listSumOfAdvertisingSaleAndSaleBudgetFree, targetIndex);
      formik.setFieldValue('monthLtAdvertising', result);
    },
    FUNCTION_INTERVAL,
  );

  // R(家賃)
  const calcLtRentDailySaleRatioValues = useDebounce(
    (listSumOfAdvertisingSaleAndSaleBudgetFree: number[], targetIndex: number) => {
      const { monthLtRent } = formik.values;

      const result = calcDaySaleRatioValues(monthLtRent, listSumOfAdvertisingSaleAndSaleBudgetFree, targetIndex);
      formik.setFieldValue('monthLtRent', result);
    },
    FUNCTION_INTERVAL,
  );

  // O(その他経費)
  const calcLtExpensesDailySaleRatioValues = useDebounce(
    (listSumOfAdvertisingSaleAndSaleBudgetFree: number[], targetIndex: number) => {
      const { monthLtExpenses } = formik.values;

      const result = calcDaySaleRatioValues(monthLtExpenses, listSumOfAdvertisingSaleAndSaleBudgetFree, targetIndex);
      formik.setFieldValue('monthLtExpenses', result);
    },
    FUNCTION_INTERVAL,
  );

  // NT(集計外項目)
  const calcLtOtherDailySaleRatioValues = useDebounce(
    (listSumOfAdvertisingSaleAndSaleBudgetFree: number[], targetIndex: number) => {
      const { monthLtOther } = formik.values;

      const result = calcDaySaleRatioValues(monthLtOther, listSumOfAdvertisingSaleAndSaleBudgetFree, targetIndex);
      formik.setFieldValue('monthLtOther', result);
    },
    FUNCTION_INTERVAL,
  );

  // 客単価
  const calcLtCustomPriceDailySaleRatioValues = useDebounce(() => {
    const { monthLtCustomPrice } = formik.values;

    if (monthLtCustomPrice) {
      const result = calcDaySaleRatioValues([monthLtCustomPrice], [monthLtCustomPrice.dayTotal], 0);
      formik.setFieldValue('monthLtCustomPrice', result[0]);
    }
  }, FUNCTION_INTERVAL);

  // 売上高
  const calcLtSalesMonthlyValue = useDebounce((targetIndex: number) => {
    const { monthLtSales } = formik.values;

    const result = calcMonthlyValue(monthLtSales, targetIndex);
    formik.setFieldValue('monthLtSales', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // F(食材費)
  const calcLtCostMonthlyValue = useDebounce((targetIndex: number) => {
    const { monthLtCost } = formik.values;

    const result = calcMonthlyValue(monthLtCost, targetIndex);
    formik.setFieldValue('monthLtCost', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // L(人件費)
  const calcLtLaborMonthlyValue = useDebounce((targetIndex: number) => {
    const { monthLtLabor } = formik.values;

    const result = calcMonthlyValue(monthLtLabor, targetIndex);
    formik.setFieldValue('monthLtLabor', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // A(広告費)
  const calcLtAdvertisingMonthlyValue = useDebounce((targetIndex: number) => {
    const { monthLtAdvertising } = formik.values;

    const result = calcMonthlyValue(monthLtAdvertising, targetIndex);
    formik.setFieldValue('monthLtAdvertising', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // R(家賃)
  const calcLtRentMonthlyValue = useDebounce((targetIndex: number) => {
    const { monthLtRent } = formik.values;

    const result = calcMonthlyValue(monthLtRent, targetIndex);
    formik.setFieldValue('monthLtRent', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // O(その他経費)
  const calcLtExpensesMonthlyValue = useDebounce((targetIndex: number) => {
    const { monthLtExpenses } = formik.values;

    const result = calcMonthlyValue(monthLtExpenses, targetIndex);
    formik.setFieldValue('monthLtExpenses', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // NT(集計外項目)
  const calcLtOtherMonthlyValue = useDebounce((targetIndex: number) => {
    const { monthLtOther } = formik.values;

    const result = calcMonthlyValue(monthLtOther, targetIndex);
    formik.setFieldValue('monthLtOther', result);
    calculatorTableDaily();
  }, FUNCTION_INTERVAL);

  // 客単価
  const calcLtCustomPriceMonthlyValue = useDebounce(() => {
    const { monthLtCustomPrice, salesPlanDailyDateData } = formik.values;

    if (monthLtCustomPrice) {
      const dataLength =
        monthLtCustomPrice?.dayAryList?.filter((item, i) => salesPlanDailyDateData[i].status !== BusinessStatus.Closed && item > 0)
          .length ?? 0;
      const totalCustomPrice =
        monthLtCustomPrice?.dayAryList
          ?.filter((_, i) => salesPlanDailyDateData[i].status !== BusinessStatus.Closed)
          .reduce((acc, cur) => acc + cur, 0) ?? 0;

      const dayTotal = Math.floor(dataLength > 0 ? totalCustomPrice / dataLength : 0);

      formik.setFieldValue('monthLtCustomPrice', { ...monthLtCustomPrice, dayTotal });
      calculatorTableDaily();
    }
  }, FUNCTION_INTERVAL);

  return {
    confirmModalOpen,
    closeConfirmModal,
    formik,
    getRows: salesBudget,
    isLoading,
    handleChangeSelect,
    calcSalesRatioValues,
    calcLtSalesDailyValues,
    calcLtCostDailyValues,
    calcLtLaborDailyValues,
    calcLtAdvertisingDailyValues,
    calcLtRentDailyValues,
    calcLtExpensesDailyValues,
    calcLtOtherDailyValues,
    calcLtCustomPriceDailyValues,
    calcLtSalesMonthlyValue,
    calcLtCostMonthlyValue,
    calcLtLaborMonthlyValue,
    calcLtAdvertisingMonthlyValue,
    calcLtRentMonthlyValue,
    calcLtExpensesMonthlyValue,
    calcLtOtherMonthlyValue,
    calcLtCustomPriceMonthlyValue,
  };
};

export default { useSalesBudgetMonth };
